import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangeFilterProps {
  /** ISO date string (yyyy-mm-dd) or null. */
  from: string | null;
  /** ISO date string (yyyy-mm-dd) or null. */
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
  className?: string;
}

const toIso = (d: Date | undefined) => (d ? d.toISOString().slice(0, 10) : null);

/** Date-range filter — two `Calendar` popovers side-by-side. */
export function DateRangeFilter({ from, to, onChange, className }: DateRangeFilterProps) {
  const fromDate = from ? new Date(from) : undefined;
  const toDate = to ? new Date(to) : undefined;

  const display = (() => {
    if (from && to) return `${format(new Date(from), 'MMM d')} – ${format(new Date(to), 'MMM d')}`;
    if (from) return `From ${format(new Date(from), 'MMM d')}`;
    if (to) return `Until ${format(new Date(to), 'MMM d')}`;
    return 'Date range';
  })();

  const active = !!(from || to);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-8 text-xs', active && 'border-primary text-primary', className)}
        >
          <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
          {display}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2 flex gap-2" align="start">
        <div>
          <p className="text-xs font-medium mb-1 text-muted-foreground">From</p>
          <Calendar mode="single" selected={fromDate} onSelect={(d) => onChange(toIso(d), to)} />
        </div>
        <div>
          <p className="text-xs font-medium mb-1 text-muted-foreground">To</p>
          <Calendar mode="single" selected={toDate} onSelect={(d) => onChange(from, toIso(d))} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
